import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ChatbotAiService,
  ChatbotMessage,
  QuickPrompt,
} from '../../services/chatbot-ai.service';
import { Router } from '@angular/router';

interface PendingMessage extends ChatbotMessage {
  pending?: boolean;
}

@Component({
  selector: 'app-chatbot-ai',
  templateUrl: './chatbot-ai.component.html',
  styleUrls: ['./chatbot-ai.component.css'],
})
export class ChatbotAiComponent implements AfterViewInit, OnDestroy {
  @ViewChild('messageScroll') messageScroll?: ElementRef<HTMLDivElement>;
  @ViewChild('editorBox') editorBox?: ElementRef<HTMLDivElement>;

  isOpen = false;
  isThinking = false;
  thinkingMessage = '';
  messages: PendingMessage[] = [];
  promptControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(500),
  ]);
  quickPrompts = this.chatbotService.getQuickPrompts();
  quickActions: QuickPrompt[] = [];
  showQuickActions = false;
  typingDots = ['.', '..', '...'];
  activeDotIndex = 0;
  private typingInterval: any;
  panelWidth = 441;
  panelHeight = 720;
  private readonly minWidth = 320;
  private readonly maxWidth = 520;
  private readonly minHeight = 380;
  private readonly maxHeight = 720;
  private isResizing = false;
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;

  private readonly resizeMouseMove = (event: MouseEvent) => this.handleResizeMove(event);
  private readonly resizeMouseUp = () => this.stopResize();
  constructor(private chatbotService: ChatbotAiService, private router: Router) {
    this.messages = this.chatbotService.getInitialMessages();
    const role = this.chatbotService.getUserRole();
    this.quickActions = this.chatbotService.getQuickActions(role);
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
  }

  toggleQuickActions(): void {
    this.showQuickActions = !this.showQuickActions;
  }

  ngOnDestroy(): void {
    this.detachResizeListeners();
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }

  handleEnter(event: KeyboardEvent | Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey) {
      return;
    }
    if (typeof keyboardEvent.preventDefault === 'function') {
      keyboardEvent.preventDefault();
    }
    this.submitPrompt();
  }

  submitPrompt(): void {
    if (this.promptControl.invalid || this.isThinking) {
      return;
    }
    const question = this.promptControl.value?.trim();
    if (!question) {
      return;
    }
    const promptId = this.isDashboardQuery(question) ? 'overview' : undefined;
    this.pushUserMessage(question);
    this.promptControl.reset();
    this.resetEditor();
    this.scrollToBottom();

    this.pushAssistantResponse(promptId, 1000, question);
  }

  runQuickPrompt(promptId: string, promptText: string): void {
    if (this.isThinking) {
      return;
    }

    // For supplier quick action: navigate directly to RFQ list filtered to unactioned
    if (promptId === 'qa-respond-rfq' && this.chatbotService.getUserRole() === 'supplier') {
      this.router.navigate(['/operations/supplier-rfq-list'], {
        queryParams: {
          updatedStatus: 'Unactioned',
          selectedStatus: 'Unactioned',
          label: 'RFQ',
        },
      });
      return;
    }

    this.pushUserMessage(promptText);
    this.promptControl.reset();
    this.resetEditor();
    this.scrollToBottom();
    this.pushAssistantResponse(promptId, 600, promptText);
  }

  private scrollToBottom(): void {
    if (!this.messageScroll) {
      return;
    }
    requestAnimationFrame(() => {
      const nativeEl = this.messageScroll?.nativeElement;
      if (!nativeEl) {
        return;
      }
      nativeEl.scrollTop = nativeEl.scrollHeight;
    });
  }

  private pushUserMessage(content: string): void {
    const newMessage: PendingMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date(),
    };
    this.messages.push(newMessage);
  }

  private pushAssistantResponse(promptId?: string, delay = 1000, question?: string): void {
    this.isThinking = true;
    this.thinkingMessage = promptId === 'overview' ? 'Analyzing your dashboard' : 'Typing...';
    this.startTypingAnimation();
    setTimeout(async () => {
      const response = await this.chatbotService.generateResponseFromApi(promptId, question);
      this.messages.push(response);
      this.isThinking = false;
      this.thinkingMessage = '';
      this.stopTypingAnimation();
      this.scrollToBottom();
    }, delay);
  }

  startResize(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isResizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartWidth = this.panelWidth;
    this.resizeStartHeight = this.panelHeight;
    window.addEventListener('mousemove', this.resizeMouseMove);
    window.addEventListener('mouseup', this.resizeMouseUp);
  }

  private handleResizeMove(event: MouseEvent): void {
    if (!this.isResizing) {
      return;
    }
    const deltaX = this.resizeStartX - event.clientX;
    const deltaY = event.clientY - this.resizeStartY;
    const newWidth = this.resizeStartWidth + deltaX;
    const newHeight = this.resizeStartHeight - deltaY;
    this.panelWidth = Math.min(
      this.maxWidth,
      Math.max(this.minWidth, newWidth),
    );
    this.panelHeight = Math.min(
      this.maxHeight,
      Math.max(this.minHeight, newHeight),
    );
  }

  private stopResize(): void {
    if (!this.isResizing) {
      return;
    }
    this.isResizing = false;
    this.detachResizeListeners();
  }

  private detachResizeListeners(): void {
    window.removeEventListener('mousemove', this.resizeMouseMove);
    window.removeEventListener('mouseup', this.resizeMouseUp);
  }

  private isDashboardQuery(text?: string): boolean {
    if (!text) return false;
    const lowered = text.toLowerCase();
    return lowered.includes('dashboard') || lowered.includes('overview') || lowered.includes('analytics');
  }

  openNotifications(): void {
    this.router.navigate(['/notifications-list']);
  }

  openProducts(): void {
    this.router.navigate(['/search/products']);
  }

  navigateLink(link?: { label: string; route: any[]; queryParams?: Record<string, any> }): void {
    if (!link) return;
    this.router.navigate(link.route, { queryParams: link.queryParams });
  }

  onEditorInput(event: Event): void {
    const target = event.target as HTMLDivElement;
    const text = target?.innerText || '';
    this.promptControl.setValue(text.trimStart());
    this.promptControl.markAsTouched();
  }

  private resetEditor(): void {
    if (this.editorBox?.nativeElement) {
      this.editorBox.nativeElement.innerText = '';
    }
  }

  private startTypingAnimation(): void {
    this.stopTypingAnimation();
    this.typingInterval = setInterval(() => {
      this.activeDotIndex = (this.activeDotIndex + 1) % this.typingDots.length;
    }, 400);
  }

  private stopTypingAnimation(): void {
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
      this.typingInterval = null;
      this.activeDotIndex = 0;
    }
  }

}
